import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useRef } from 'react';
const GoogleMapsContext = createContext({
    isLoaded: false,
    loadError: null,
});
export const useGoogleMaps = () => useContext(GoogleMapsContext);
// Singleton state to prevent multiple loads
let loadingPromise = null;
// Global auth-failure hook documented by Google Maps JS API.
// Fires when the script loaded but the API key is invalid / referrer-restricted /
// billing is off. Without this hook the error only surfaces as a greyed-out map
// tile and the app looks "silently broken". Install once at module scope.
// https://developers.google.com/maps/documentation/javascript/events#auth-errors
if (typeof window !== 'undefined' && !window.gm_authFailure) {
    ;
    window.gm_authFailure = () => {
        console.error('[GoogleMaps] gm_authFailure — API key invalid, referrer not whitelisted, or billing disabled. Check Cloud Console.');
    };
}
export const GoogleMapsProvider = ({ apiKey, language = 'en', libraries = ['places'], children }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        const loadScript = async () => {
            // Check if Google Maps is already loaded
            if (window.google?.maps) {
                console.log('🗺️ [GoogleMapsProvider] Google Maps already loaded:', {
                    hasGoogle: !!window.google,
                    hasMaps: !!window.google?.maps,
                    hasPlaces: !!window.google?.maps?.places
                });
                if (isMountedRef.current) {
                    setIsLoaded(true);
                }
                return;
            }
            // If already loading, wait for that promise
            if (loadingPromise) {
                try {
                    await loadingPromise;
                    if (isMountedRef.current) {
                        setIsLoaded(true);
                    }
                }
                catch (error) {
                    if (isMountedRef.current) {
                        setLoadError(error instanceof Error ? error : new Error('Failed to load Google Maps'));
                    }
                }
                return;
            }
            // Check if script tag already exists
            const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
            if (existingScript) {
                // Script exists, wait for it to load
                loadingPromise = new Promise((resolve, reject) => {
                    existingScript.addEventListener('load', () => resolve());
                    existingScript.addEventListener('error', reject);
                });
                try {
                    await loadingPromise;
                    if (isMountedRef.current) {
                        setIsLoaded(true);
                    }
                }
                catch (error) {
                    if (isMountedRef.current) {
                        setLoadError(error instanceof Error ? error : new Error('Failed to load Google Maps'));
                    }
                }
                return;
            }
            // Create new loading promise
            loadingPromise = new Promise((resolve, reject) => {
                // Build query params step-by-step (URLSearchParams doesn't support spread operator)
                const params = new URLSearchParams();
                params.set('key', apiKey);
                params.set('libraries', libraries.join(','));
                if (language) {
                    params.set('language', language);
                }
                params.set('loading', 'async'); // Best practice for performance
                const scriptUrl = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
                // 🔍 DIAGNOSTIC LOGGING
                console.log('🗺️ [GoogleMapsProvider] Loading script:', {
                    url: scriptUrl,
                    apiKeyLength: apiKey?.length || 0,
                    apiKeyPreview: apiKey ? `${apiKey.substring(0, 10)}...` : 'MISSING',
                    libraries: libraries.join(','),
                    language
                });
                const script = document.createElement('script');
                script.src = scriptUrl;
                script.async = true;
                script.defer = true;
                script.id = 'google-maps-script'; // Add ID for easier debugging
                script.onload = () => {
                    console.log('🗺️ [GoogleMapsProvider] Script loaded successfully, google.maps:', !!window.google?.maps);
                    resolve();
                };
                script.onerror = (error) => {
                    console.error('❌ [GoogleMapsProvider] Script load error:', error);
                    loadingPromise = null; // Reset on error to allow retry
                    reject(new Error('Failed to load Google Maps'));
                };
                document.head.appendChild(script);
            });
            try {
                await loadingPromise;
                if (isMountedRef.current) {
                    console.log('✅ [GoogleMapsProvider] isLoaded set to true');
                    setIsLoaded(true);
                }
            }
            catch (error) {
                console.error('❌ [GoogleMapsProvider] Load error:', error);
                if (isMountedRef.current) {
                    setLoadError(error instanceof Error ? error : new Error('Failed to load Google Maps'));
                }
            }
        };
        loadScript();
        return () => {
            isMountedRef.current = false;
        };
    }, []); // Empty deps - only load once per app lifecycle
    return (_jsx(GoogleMapsContext.Provider, { value: { isLoaded, loadError }, children: children }));
};
